import { Carrot } from "@/components/Carrot";
import { FeatureRelay } from "@/components/FeatureRelay";
import { OrganicUnderline } from "@/components/OrganicLines";
import { Reveal } from "@/components/Reveal";
import { ChatShowcase } from "@/components/sections/ChatShowcase";

// The root, annotated: what actually lives in the recovered memory. Kept to
// the capabilities the product ships today (see the screenshots below).
const SYSTEM_BRANCHES = [
  {
    number: "01",
    label: "The horse record",
    body: "Profiles, registry sync, lineage, photos, video, awards and location history.",
    position: "lg:left-0 lg:top-3",
  },
  {
    number: "02",
    label: "Daily operations",
    body: "Calendar, care activities, locations, feeding, ration plans and inventory.",
    position: "lg:left-2 lg:bottom-4",
  },
  {
    number: "03",
    label: "Money & accountability",
    body: "Income, expenses, boarding agreements, expected charges and reconciliation.",
    position: "lg:right-0 lg:top-3",
  },
  {
    number: "04",
    label: "Shared intelligence",
    body: "Document import, source-backed facts, barn knowledge, chat and hands-free voice.",
    position: "lg:right-2 lg:bottom-4",
  },
];

const MEMORY_INPUTS = ["Files", "Phones", "Routines", "People"] as const;

const STAGE_STYLES = {
  Today: "bg-carrot font-bold text-ink",
  Next: "bg-cream/15 text-cream",
  Later: "border border-cream/30 text-cream/70",
} as const;

const LAYERS = [
  {
    number: "01",
    name: "Capture",
    stage: "Today",
    role: "Docs, PDFs, spreadsheets, photos, voice and chat. Tomorrow, cameras.",
  },
  {
    number: "02",
    name: "Understand",
    stage: "Today",
    role: "Resolve horses, people, events and costs into source-backed facts.",
  },
  {
    number: "03",
    name: "Operate",
    stage: "Next",
    role: "Due lists, reminders, owner updates and exception workflows.",
  },
  {
    number: "04",
    name: "Verify",
    stage: "Later",
    role: "Records of care, training and health that travel with the horse.",
  },
] as const;

const PRINTS = [
  {
    src: "/static/screenshot2.png",
    w: 3840,
    h: 2160,
    label: "Feed it a document",
    caption: "Drop in a registration PDF. The facts come back structured, source-backed, ready to use.",
    alt: "The CarrotOnTheHorse assistant reading an uploaded registration certificate and extracting the horse's details",
    frame: "lg:-rotate-[1.4deg]",
    pin: "-7deg",
  },
  {
    src: "/static/horses-page.jpg",
    w: 1600,
    h: 900,
    label: "Get the whole barn back",
    caption: "Every horse on the place, searchable and filterable: photo, status, paddock and care at a glance.",
    alt: "The CarrotOnTheHorse barn view listing every horse with photo, status and care details",
    frame: "lg:rotate-[1.4deg]",
    pin: "8deg",
  },
] as const;

const CHIPS = ["Tables", "Forms", "CSV export", "Spreadsheet import", "Shareable"];

/**
 * Section 4 of the descent: the root. The darkest ground on the page, and the
 * only place the brand orange lives underground — the buried thing the whole
 * page has been digging toward. All the product proof is here.
 */
export function Root() {
  return (
    <section id="root-system" className="root-field relative overflow-hidden bg-field-deep text-cream">
      <div className="relative mx-auto max-w-7xl px-5 pt-24 sm:px-8 lg:pt-28">
        <div className="root-intro grid items-center gap-14 lg:grid-cols-[0.38fr_0.62fr] lg:gap-10 xl:gap-16">
          <Reveal className="max-w-xl lg:pb-8">
            <p className="text-[0.68rem] font-bold uppercase tracking-[0.24em] text-carrot-bright">Below the surface</p>
            <h2 className="mt-5 text-4xl font-extrabold leading-[0.96] tracking-[-0.04em] text-cream sm:text-5xl xl:text-[3.65rem]">
              Your barn already has a{" "}
              <span className="relative inline-block text-carrot">
                nervous system.
                <OrganicUnderline className="absolute -bottom-[0.1em] left-0 h-[0.2em] w-full text-carrot-bright/65" />
              </span>
            </h2>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-cream/72">
              CarrotOnTheHorse resolves the fragments your barn already makes into one living model, and puts
              the whole operation within reach.
            </p>

            <div className="mt-9 border-y border-cream/12 py-4">
              <p className="mb-3 text-[0.62rem] font-bold uppercase tracking-[0.2em] text-cream/65">What it reads</p>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4 lg:grid-cols-2">
                {MEMORY_INPUTS.map((input) => (
                  <li key={input} className="flex items-center gap-2 text-sm font-semibold text-cream/78">
                    <span className="size-1.5 rounded-full bg-carrot" />
                    {input}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* A literal product root system: four working product domains connected to one memory.
              `stage`: the map doesn't fade as one block — the branch lines draw and the four
              nodes stagger in on their own. */}
          <Reveal stage>
            <div className="root-map relative mx-auto min-h-[43rem] w-full max-w-3xl lg:min-h-[35rem]">
            <svg aria-hidden viewBox="0 0 1000 650" preserveAspectRatio="none" className="absolute inset-0 hidden size-full lg:block">
              <defs>
                <mask id="root-network-reveal" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="650">
                  <g fill="none" stroke="white" strokeWidth="12" strokeLinecap="round">
                    <path pathLength={1} className="root-mask root-mask-1" d="M495 330 C390 315 355 145 235 120 C150 103 115 86 70 54" />
                    <path pathLength={1} className="root-mask root-mask-2" d="M485 350 C385 365 345 515 220 545 C148 562 112 585 78 618" />
                    <path pathLength={1} className="root-mask root-mask-3" d="M505 330 C610 315 645 145 765 120 C850 103 885 86 930 54" />
                    <path pathLength={1} className="root-mask root-mask-4" d="M515 350 C615 365 655 515 780 545 C852 562 888 585 922 618" />
                    <path pathLength={1} className="root-mask root-mask-1" d="M500 335 C450 245 475 165 415 88" />
                    <path pathLength={1} className="root-mask root-mask-2" d="M500 346 C550 245 525 165 585 88" />
                    <path pathLength={1} className="root-mask root-mask-3" d="M500 350 C455 430 465 505 402 588" />
                    <path pathLength={1} className="root-mask root-mask-4" d="M500 350 C545 430 535 505 598 588" />
                  </g>
                </mask>
              </defs>
              <g fill="none" stroke="currentColor" strokeLinecap="round" mask="url(#root-network-reveal)">
                <path pathLength={1} className="root-branch root-branch-1" d="M495 330 C390 315 355 145 235 120 C150 103 115 86 70 54" />
                <path pathLength={1} className="root-branch root-branch-2" d="M485 350 C385 365 345 515 220 545 C148 562 112 585 78 618" />
                <path pathLength={1} className="root-branch root-branch-3" d="M505 330 C610 315 645 145 765 120 C850 103 885 86 930 54" />
                <path pathLength={1} className="root-branch root-branch-4" d="M515 350 C615 365 655 515 780 545 C852 562 888 585 922 618" />
                <path pathLength={1} className="root-fiber root-fiber-soft root-fiber-1" d="M500 335 C450 245 475 165 415 88" />
                <path pathLength={1} className="root-fiber root-fiber-soft root-fiber-2" d="M500 346 C550 245 525 165 585 88" />
                <path pathLength={1} className="root-fiber root-fiber-soft root-fiber-3" d="M500 350 C455 430 465 505 402 588" />
                <path pathLength={1} className="root-fiber root-fiber-soft root-fiber-4" d="M500 350 C545 430 535 505 598 588" />
              </g>
            </svg>

            <div className="relative z-10 grid gap-4 lg:absolute lg:inset-0 lg:block">
              {SYSTEM_BRANCHES.map((branch) => (
                <article key={branch.number} className={`root-node lg:absolute lg:w-[11.5rem] xl:w-[14.5rem] ${branch.position}`}>
                  <div className="root-node-core">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs font-bold text-carrot">{branch.number}</span>
                      <span className="h-px flex-1 bg-cream/12" />
                    </div>
                    <h3 className="mt-4 text-lg font-bold leading-tight text-cream xl:text-xl">{branch.label}</h3>
                    <p className="mt-2.5 text-[0.82rem] leading-relaxed text-cream/62">{branch.body}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="root-memory-core relative z-10 mx-auto mt-7 flex w-56 flex-col items-center lg:absolute lg:left-1/2 lg:top-1/2 lg:mt-0 lg:-translate-x-1/2 lg:-translate-y-1/2">
              <span className="root-memory-label mb-2 text-[0.62rem] font-bold uppercase tracking-[0.24em] text-cream/65">One operational memory</span>
              <Carrot aria-hidden className="root-carrot h-[14rem] w-44 drop-shadow-[0_32px_50px_rgba(0,0,0,0.28)] sm:h-[16rem] sm:w-48 lg:h-[17rem] lg:w-44 xl:h-[19rem] xl:w-52" />
              <span className="root-memory-chip mt-2 rounded-full bg-carrot px-4 py-2 text-xs font-extrabold uppercase tracking-[0.16em] text-ink">
                Import, not input
              </span>
            </div>
          </div>
          </Reveal>
        </div>

      </div>

      {/* the paper band leaves the constrained column: its wavy edges run the
          full section width, like the page's other ground-to-ground cuts */}
      <Reveal className="mt-20 lg:mt-24" delay={80}>
        <FeatureRelay />
      </Reveal>

      <div className="relative mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-28">
        {/* how it works */}
        <div id="how" className="mt-20 lg:mt-24">
          <Reveal className="max-w-2xl">
            <h3 className="text-3xl font-extrabold leading-[1.05] sm:text-4xl">Import, not input.</h3>
            <p className="mt-4 text-lg leading-relaxed text-cream/75">
              The structure builds itself as a side effect of the work: no forms, no data entry. Capture and
              understanding run today; operating and verified history come next.
            </p>
          </Reveal>

          <ol className="mt-12 border-y border-cream/12 lg:grid lg:grid-cols-4">
            {LAYERS.map((layer, i) => (
              <Reveal key={layer.name} delay={(i % 4) * 100}>
                <li className={`group h-full py-7 lg:px-7 lg:py-9 ${i ? "border-t border-cream/12 lg:border-l lg:border-t-0" : ""}`}>
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-mono text-sm font-bold text-carrot-bright">{layer.number}</span>
                    <span className={`rounded-full px-2.5 py-1 text-xs ${STAGE_STYLES[layer.stage]}`}>
                      {layer.stage}
                    </span>
                  </div>
                  <h4 className="root-layer-title mt-8 text-xl font-bold text-cream">{layer.name}</h4>
                  <p className="mt-2.5 text-[0.95rem] leading-relaxed text-cream/70">{layer.role}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>

        {/* the proof: real product, pinned like prints on the barn wall */}
        <div id="forms" className="mt-28">
          <div className="grid gap-x-8 gap-y-16 pt-4 lg:grid-cols-2">
            {PRINTS.map((shot, i) => (
              <Reveal key={shot.src} delay={i * 140}>
                <figure className="relative">
                  <Carrot
                    aria-hidden
                    className="absolute -top-5 left-1/2 z-10 h-12 w-12 drop-shadow-[0_8px_10px_rgba(0,0,0,0.35)]"
                    style={{ transform: `translateX(-50%) rotate(${shot.pin})` }}
                  />
                  <div
                    className={`root-proof-frame overflow-hidden rounded-[1.4rem] border-[6px] border-paper bg-paper shadow-[0_38px_70px_-32px_rgba(0,0,0,0.55)] ${shot.frame}`}
                  >
                    <img
                      src={shot.src}
                      alt={shot.alt}
                      width={shot.w}
                      height={shot.h}
                      loading="lazy"
                      decoding="async"
                      className="block w-full rounded-[0.7rem]"
                    />
                  </div>
                  <figcaption className="mt-6">
                    <span className="text-base font-bold text-cream">{shot.label}</span>
                    <p className="mt-2 max-w-md leading-relaxed text-cream/70">{shot.caption}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>

        {/* the demo: watch the recovery happen */}
        <div id="demo" className="mt-28">
          <Reveal className="max-w-2xl">
            <h3 className="text-3xl font-extrabold leading-[1.05] sm:text-4xl">
              Watch it read a farrier log.
            </h3>
            <p className="mt-4 text-lg leading-relaxed text-cream/75">
              One spreadsheet in: it finds 137 horses, asks about the 16 it isn't sure of, and files 492 care
              actions against the right animals. Then you just ask the herd questions.
            </p>
          </Reveal>
          <Reveal delay={120} className="mt-10">
            <ChatShowcase />
          </Reveal>
          <p className="mt-4 text-center text-sm text-cream/60">
            A staged example. Names and records are fictional.
          </p>
        </div>

        {/* and underneath, still a real app */}
        <div className="mt-28 grid items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <h3 className="text-3xl font-extrabold leading-[1.05] sm:text-4xl">
              Sometimes you just want the form.
            </h3>
            <p className="mt-4 text-lg leading-relaxed text-cream/75">
              Underneath the AI it's still a real app. Punch in numbers yourself, keep a tidy inventory, or
              export a clean sheet for your vet or accountant whenever you'd rather.
            </p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {CHIPS.map((c) => (
                <li
                  key={c}
                  className="inline-flex rounded-full border border-cream/25 bg-cream/[0.06] px-3.5 py-1.5 text-sm font-semibold text-cream/85"
                >
                  {c}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120}>
            <div className="overflow-hidden rounded-2xl border border-cream/15 bg-paper shadow-[0_38px_70px_-32px_rgba(0,0,0,0.55)]">
              <img
                src="/static/horse-profile.jpg"
                alt="A single horse's profile in CarrotOnTheHorse with vitals, owner, farrier due dates and location"
                width={1600}
                height={900}
                loading="lazy"
                decoding="async"
                className="block w-full"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
